const PAGE_WIDTH = 612
const PAGE_HEIGHT = 792
const MARGIN_X = 48
const ACCENT_RED = [180, 24, 45]
const ACCENT_RED_LIGHT = [252, 242, 244]
const WARNING_TEXT = [107, 16, 32]

const escapePdfText = (value) =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")

const rgb = (r, g, b) =>
  `${(r / 255).toFixed(3)} ${(g / 255).toFixed(3)} ${(b / 255).toFixed(3)}`

const formatCode = (code) => {
  const digits = String(code).replace(/\D/g, "")
  if (digits.length === 16) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(
      8,
      12,
    )} ${digits.slice(12)}`
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`
  }
  return String(code)
}

const buildContentStream = (codes) => {
  const commands = []
  const push = (...lines) => commands.push(...lines)

  const fillColor = (r, g, b) => push(`${rgb(r, g, b)} rg`)
  const strokeColor = (r, g, b) => push(`${rgb(r, g, b)} RG`)
  const rect = (x, y, w, h) => push(`${x} ${y} ${w} ${h} re`)
  const fill = () => push("f")
  const stroke = () => push("S")
  const fillStroke = () => push("B")

  const text = (font, size, x, y, value, color = [17, 17, 17]) => {
    push("BT")
    push(`${font} ${size} Tf`)
    push(`${rgb(...color)} rg`)
    push(`1 0 0 1 ${x} ${y} Tm`)
    push(`(${escapePdfText(value)}) Tj`)
    push("ET")
  }

  // Header band
  fillColor(...ACCENT_RED)
  rect(0, PAGE_HEIGHT - 78, PAGE_WIDTH, 78)
  fill()

  text(
    "/F2",
    18,
    MARGIN_X,
    PAGE_HEIGHT - 36,
    "BRIDGE 2 TECHNOLOGIES",
    [255, 255, 255],
  )
  text(
    "/F2",
    13,
    MARGIN_X,
    PAGE_HEIGHT - 56,
    "MFA Recovery Codes",
    [255, 230, 232],
  )
  text(
    "/F1",
    10,
    MARGIN_X,
    PAGE_HEIGHT - 72,
    "Multi-factor authentication backup codes",
    [255, 230, 232],
  )

  // Intro
  text(
    "/F1",
    11,
    MARGIN_X,
    PAGE_HEIGHT - 108,
    "Keep this document in a secure location. Each code below can be used only once",
    [60, 60, 60],
  )
  text(
    "/F1",
    11,
    MARGIN_X,
    PAGE_HEIGHT - 124,
    "when signing in if you cannot access your email.",
    [60, 60, 60],
  )

  // Warning box
  const warningBottom = PAGE_HEIGHT - 178
  fillColor(...ACCENT_RED_LIGHT)
  strokeColor(...ACCENT_RED)
  rect(MARGIN_X - 4, warningBottom, PAGE_WIDTH - MARGIN_X * 2 + 8, 40)
  fillStroke()
  text(
    "/F2",
    10,
    MARGIN_X + 8,
    warningBottom + 24,
    "Important: Regenerating recovery codes invalidates every code in this list.",
    WARNING_TEXT,
  )

  // Code grid header
  text(
    "/F2",
    12,
    MARGIN_X,
    warningBottom - 28,
    "Your recovery codes",
    [26, 26, 26],
  )
  text(
    "/F1",
    10,
    MARGIN_X,
    warningBottom - 44,
    `${codes.length} one-time codes · use at MFA login`,
    [100, 100, 100],
  )

  const colWidth = PAGE_WIDTH - MARGIN_X * 2
  const rowHeight = 46
  const gridTop = warningBottom - 72

  codes.forEach((code, index) => {
    const x = MARGIN_X
    const boxBottom = gridTop - index * rowHeight - 30

    fillColor(250, 250, 250)
    strokeColor(224, 224, 224)
    rect(x, boxBottom, colWidth, 34)
    fillStroke()

    fillColor(...ACCENT_RED)
    rect(x, boxBottom, 28, 34)
    fill()

    text("/F2", 10, x + 9, boxBottom + 12, String(index + 1), [255, 255, 255])
    text("/F2", 13, x + 40, boxBottom + 12, formatCode(code), [17, 17, 17])
  })

  // Footer divider
  strokeColor(230, 230, 230)
  push("0.5 w")
  push(`${MARGIN_X} 72 m ${PAGE_WIDTH - MARGIN_X} 72 l S`)

  const generatedAt = new Date().toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

  text("/F1", 9, MARGIN_X, 52, `Generated: ${generatedAt}`, [120, 120, 120])
  text(
    "/F1",
    9,
    MARGIN_X,
    38,
    "BRIDGE 2 TECHNOLOGIES · Do not share these codes with anyone.",
    [120, 120, 120],
  )

  return commands.join("\n")
}

const assemblePdf = (contentStream) => {
  const contentLength = contentStream.length

  const objects = [
    "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n",
    "2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n",
    [
      "3 0 obj\n<< /Type /Page /Parent 2 0 R",
      `/MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}]`,
      "/Contents 4 0 R",
      "/Resources << /Font << /F1 5 0 R /F2 6 0 R >> >>",
      "/Producer (BRIDGE 2 TECHNOLOGIES)",
      ">>\nendobj\n",
    ].join(" "),
    `4 0 obj\n<< /Length ${contentLength} >>\nstream\n${contentStream}\nendstream\nendobj\n`,
    "5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n",
    "6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n",
  ]

  let pdf = "%PDF-1.4\n"
  const offsets = [0]

  objects.forEach((obj) => {
    offsets.push(pdf.length)
    pdf += obj
  })

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n`
  pdf += "0000000000 65535 f \n"
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`
  })
  pdf += `trailer\n<< /Size ${
    objects.length + 1
  } /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return pdf
}

export const buildRecoveryCodesPdfBlob = (codes) => {
  const contentStream = buildContentStream(codes)
  const pdf = assemblePdf(contentStream)
  return new Blob([pdf], { type: "application/pdf" })
}

export const downloadRecoveryCodesPdf = (codes) => {
  const blob = buildRecoveryCodesPdfBlob(codes)
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = "bridge-2-technologies-mfa-recovery-codes.pdf"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
